import React from 'react';
import Card from '../../ui/Card';
import DescriptionPanel from '../../ui/DescriptionPanel';
import { TrendingUpIcon, TrendingDownIcon } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
const CapacityUtilizationDashboard: React.FC = () => {
  // KPI Data
  const kpis = [{
    title: 'Overall Utilization',
    value: '82.5%',
    change: '+3.2%',
    isPositive: true,
    subtitle: 'Across all lines'
  }, {
    title: 'Idle Time',
    value: '17.5%',
    change: '-3.2%',
    isPositive: true,
    subtitle: 'Average downtime'
  }, {
    title: 'Peak Utilization',
    value: '95.2%',
    change: '+1.8%',
    isPositive: true,
    subtitle: 'Best performing line'
  }, {
    title: 'Capacity Gap',
    value: '12.5%',
    change: '-2.1%',
    isPositive: true,
    subtitle: 'Available capacity'
  }];
  // Planned vs Actual Utilization Data
  const plannedVsActualData = [{
    line: 'Line 1',
    planned: 85,
    actual: 82,
    gap: 3
  }, {
    line: 'Line 2',
    planned: 90,
    actual: 88,
    gap: 2
  }, {
    line: 'Line 3',
    planned: 80,
    actual: 76,
    gap: 4
  }, {
    line: 'Line 4',
    planned: 87,
    actual: 84,
    gap: 3
  }, {
    line: 'Line 5',
    planned: 83,
    actual: 80,
    gap: 3
  }];
  // Line Capacity Bar Chart Data
  const lineCapacityData = [{
    line: 'Line 1',
    utilized: 82,
    idle: 18,
    maxCapacity: 100,
    currentOutput: 6580,
    maxOutput: 8000
  }, {
    line: 'Line 2',
    utilized: 88,
    idle: 12,
    maxCapacity: 100,
    currentOutput: 7040,
    maxOutput: 8000
  }, {
    line: 'Line 3',
    utilized: 76,
    idle: 24,
    maxCapacity: 100,
    currentOutput: 6080,
    maxOutput: 8000
  }, {
    line: 'Line 4',
    utilized: 84,
    idle: 16,
    maxCapacity: 100,
    currentOutput: 6720,
    maxOutput: 8000
  }, {
    line: 'Line 5',
    utilized: 80,
    idle: 20,
    maxCapacity: 100,
    currentOutput: 6400,
    maxOutput: 8000
  }];
  // Operator Load Distribution Data
  const operatorLoadData = [{
    shift: 'Shift A',
    operators: 45,
    avgLoad: 87,
    peakLoad: 95,
    minLoad: 78
  }, {
    shift: 'Shift B',
    operators: 48,
    avgLoad: 92,
    peakLoad: 98,
    minLoad: 85
  }, {
    shift: 'Shift C',
    operators: 42,
    avgLoad: 78,
    peakLoad: 88,
    minLoad: 68
  }];
  // Operator Load Radar Data
  const operatorRadarData = [{
    metric: 'Productivity',
    ShiftA: 87,
    ShiftB: 92,
    ShiftC: 78
  }, {
    metric: 'Quality',
    ShiftA: 94,
    ShiftB: 96,
    ShiftC: 88
  }, {
    metric: 'Efficiency',
    ShiftA: 89,
    ShiftB: 93,
    ShiftC: 82
  }, {
    metric: 'Utilization',
    ShiftA: 85,
    ShiftB: 88,
    ShiftC: 78
  }, {
    metric: 'Availability',
    ShiftA: 91,
    ShiftB: 94,
    ShiftC: 85
  }];
  const plantUtilizationData = [{
    plant: 'Manesar',
    planned: 85,
    actual: 82,
    available: 15
  }, {
    plant: 'Chennai',
    planned: 90,
    actual: 88,
    available: 10
  }, {
    plant: 'Pune',
    planned: 80,
    actual: 76,
    available: 20
  }, {
    plant: 'Bangalore',
    planned: 87,
    actual: 84,
    available: 13
  }];
  const machineLoadingData = [{
    machine: 'CNC-1',
    loading: 92,
    idle: 8
  }, {
    machine: 'CNC-2',
    loading: 88,
    idle: 12
  }, {
    machine: 'Injection-1',
    loading: 95,
    idle: 5
  }, {
    machine: 'Injection-2',
    loading: 90,
    idle: 10
  }, {
    machine: 'Assembly-1',
    loading: 78,
    idle: 22
  }, {
    machine: 'Assembly-2',
    loading: 82,
    idle: 18
  }];
  const shiftPerformanceData = [{
    shift: 'Shift A (6-14)',
    utilization: 85,
    output: 11200
  }, {
    shift: 'Shift B (14-22)',
    utilization: 88,
    output: 11600
  }, {
    shift: 'Shift C (22-6)',
    utilization: 78,
    output: 10300
  }];
  const monthlyUtilizationData = [{
    month: 'Jan',
    utilization: 76,
    capacity: 90
  }, {
    month: 'Feb',
    utilization: 78,
    capacity: 90
  }, {
    month: 'Mar',
    utilization: 79,
    capacity: 90
  }, {
    month: 'Apr',
    utilization: 80,
    capacity: 90
  }, {
    month: 'May',
    utilization: 81,
    capacity: 90
  }, {
    month: 'Jun',
    utilization: 82,
    capacity: 90
  }, {
    month: 'Jul',
    utilization: 83,
    capacity: 90
  }, {
    month: 'Aug',
    utilization: 84,
    capacity: 90
  }, {
    month: 'Sep',
    utilization: 82,
    capacity: 90
  }, {
    month: 'Oct',
    utilization: 85,
    capacity: 90
  }, {
    month: 'Nov',
    utilization: 86,
    capacity: 90
  }, {
    month: 'Dec',
    utilization: 87,
    capacity: 90
  }];
  // Utilization Summary Table Data
  const utilizationSummaryData = [{
    line: 'Line 1',
    shift: 'Shift A',
    planned: 85,
    actual: 82,
    idle: 18,
    utilization: 82,
    output: 3750,
    capacity: 4000,
    efficiency: 93.8,
    bottleneck: 'Material Supply'
  }, {
    line: 'Line 1',
    shift: 'Shift B',
    planned: 85,
    actual: 83,
    idle: 17,
    utilization: 83,
    output: 3900,
    capacity: 4000,
    efficiency: 97.5,
    bottleneck: 'None'
  }, {
    line: 'Line 1',
    shift: 'Shift C',
    planned: 85,
    actual: 78,
    idle: 22,
    utilization: 78,
    output: 3450,
    capacity: 4000,
    efficiency: 86.3,
    bottleneck: 'Operator Shortage'
  }, {
    line: 'Line 2',
    shift: 'Shift A',
    planned: 90,
    actual: 87,
    idle: 13,
    utilization: 87,
    output: 3850,
    capacity: 4000,
    efficiency: 96.3,
    bottleneck: 'None'
  }, {
    line: 'Line 2',
    shift: 'Shift B',
    planned: 90,
    actual: 89,
    idle: 11,
    utilization: 89,
    output: 4050,
    capacity: 4000,
    efficiency: 101.3,
    bottleneck: 'None'
  }, {
    line: 'Line 2',
    shift: 'Shift C',
    planned: 90,
    actual: 85,
    idle: 15,
    utilization: 85,
    output: 3600,
    capacity: 4000,
    efficiency: 90.0,
    bottleneck: 'Equipment Maintenance'
  }, {
    line: 'Line 3',
    shift: 'Shift A',
    planned: 80,
    actual: 75,
    idle: 25,
    utilization: 75,
    output: 3450,
    capacity: 4000,
    efficiency: 86.3,
    bottleneck: 'Quality Issues'
  }, {
    line: 'Line 3',
    shift: 'Shift B',
    planned: 80,
    actual: 77,
    idle: 23,
    utilization: 77,
    output: 3550,
    capacity: 4000,
    efficiency: 88.8,
    bottleneck: 'None'
  }, {
    line: 'Line 3',
    shift: 'Shift C',
    planned: 80,
    actual: 72,
    idle: 28,
    utilization: 72,
    output: 3200,
    capacity: 4000,
    efficiency: 80.0,
    bottleneck: 'Power Issues'
  }, {
    line: 'Line 4',
    shift: 'Shift A',
    planned: 87,
    actual: 83,
    idle: 17,
    utilization: 83,
    output: 3700,
    capacity: 4000,
    efficiency: 92.5,
    bottleneck: 'None'
  }, {
    line: 'Line 4',
    shift: 'Shift B',
    planned: 87,
    actual: 85,
    idle: 15,
    utilization: 85,
    output: 3850,
    capacity: 4000,
    efficiency: 96.3,
    bottleneck: 'None'
  }, {
    line: 'Line 4',
    shift: 'Shift C',
    planned: 87,
    actual: 80,
    idle: 20,
    utilization: 80,
    output: 3400,
    capacity: 4000,
    efficiency: 85.0,
    bottleneck: 'Tool Change'
  }, {
    line: 'Line 5',
    shift: 'Shift A',
    planned: 83,
    actual: 79,
    idle: 21,
    utilization: 79,
    output: 3600,
    capacity: 4000,
    efficiency: 90.0,
    bottleneck: 'None'
  }, {
    line: 'Line 5',
    shift: 'Shift B',
    planned: 83,
    actual: 81,
    idle: 19,
    utilization: 81,
    output: 3750,
    capacity: 4000,
    efficiency: 93.8,
    bottleneck: 'None'
  }, {
    line: 'Line 5',
    shift: 'Shift C',
    planned: 83,
    actual: 76,
    idle: 24,
    utilization: 76,
    output: 3350,
    capacity: 4000,
    efficiency: 83.8,
    bottleneck: 'Shift Changeover'
  }];
  return <div data-id="element-4868">
      <DescriptionPanel title="Capacity Utilization Analysis" description="Monitor planned vs actual capacity utilization across plants, machines, and shifts. Identify underutilized assets and optimize production scheduling for maximum throughput." insights={['Overall utilization at 82.5% with 17.5% idle time - opportunity for improvement', 'Line 2 leading with 88% utilization - best practices to replicate', 'Shift B performing best with 92% operator load and highest efficiency', 'Line 3 showing highest idle time at 24% - requires immediate attention']} data-id="element-4869" />
      {/* KPIs */}
      <div className="mb-8" data-id="element-4870">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-4871">
          Capacity Utilization Metrics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-id="element-4872">
          {kpis.map((kpi, index) => <Card key={index} className="p-5 bg-gradient-to-br from-white to-gray-50" data-id="element-4873">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide" data-id="element-4874">
                {kpi.title}
              </div>
              <div className="text-xs text-gray-400 mb-2" data-id="element-4875">{kpi.subtitle}</div>
              <div className="text-3xl font-bold text-gray-900 mb-2" data-id="element-4876">
                {kpi.value}
              </div>
              <div className={`text-sm flex items-center font-semibold ${kpi.isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-4877">
                {kpi.isPositive ? <TrendingUpIcon size={16} className="mr-1" data-id="element-4878" /> : <TrendingDownIcon size={16} className="mr-1" data-id="element-4879" />}
                {kpi.change}
              </div>
            </Card>)}
        </div>
      </div>
      {/* Planned vs Actual Utilization */}
      <Card className="p-6 mb-8" data-id="element-4880">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-4881">
          Planned vs Actual Utilization (%)
        </h3>
        <ResponsiveContainer width="100%" height={350} data-id="element-4882">
          <ComposedChart data={plannedVsActualData} data-id="element-4883">
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-4884" />
            <XAxis dataKey="line" tick={{
            fontSize: 12
          }} data-id="element-4885" />
            <YAxis tick={{
            fontSize: 12
          }} domain={[0, 100]} data-id="element-4886" />
            <Tooltip contentStyle={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px'
          }} data-id="element-4887" />
            <Legend data-id="element-4888" />
            <Bar dataKey="planned" fill="#94a3b8" name="Planned %" data-id="element-4889" />
            <Bar dataKey="actual" fill="#3b82f6" name="Actual %" data-id="element-4890" />
            <Line type="monotone" dataKey="gap" stroke="#ef4444" strokeWidth={2} name="Gap %" dot={{
            r: 5
          }} data-id="element-4891" />
          </ComposedChart>
        </ResponsiveContainer>
        <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-4892">
          <div className="grid grid-cols-3 gap-4 text-sm" data-id="element-4893">
            <div className="text-center" data-id="element-4894">
              <div className="text-gray-600" data-id="element-4895">Avg Planned</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-4896">85%</div>
            </div>
            <div className="text-center" data-id="element-4897">
              <div className="text-gray-600" data-id="element-4898">Avg Actual</div>
              <div className="text-xl font-bold text-blue-600" data-id="element-4899">82%</div>
            </div>
            <div className="text-center" data-id="element-4900">
              <div className="text-gray-600" data-id="element-4901">Avg Gap</div>
              <div className="text-xl font-bold text-red-600" data-id="element-4902">3%</div>
            </div>
          </div>
        </div>
      </Card>
      {/* Line Capacity Bar Chart and Operator Load Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-id="element-4903">
        {/* Line Capacity Bar Chart */}
        <Card className="p-6" data-id="element-4904">
          <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-4905">
            Line Capacity Analysis
          </h3>
          <ResponsiveContainer width="100%" height={350} data-id="element-4906">
            <BarChart data={lineCapacityData} layout="vertical" data-id="element-4907">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-4908" />
              <XAxis type="number" domain={[0, 100]} tick={{
              fontSize: 11
            }} data-id="element-4909" />
              <YAxis dataKey="line" type="category" width={60} tick={{
              fontSize: 11
            }} data-id="element-4910" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} data-id="element-4911" />
              <Legend data-id="element-4912" />
              <Bar dataKey="utilized" fill="#10b981" name="Utilized %" stackId="a" data-id="element-4913" />
              <Bar dataKey="idle" fill="#ef4444" name="Idle %" stackId="a" data-id="element-4914" />
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-4915">
            <div className="space-y-2" data-id="element-4916">
              {lineCapacityData.map((line, index) => <div key={index} className="flex justify-between items-center text-sm" data-id="element-4917">
                  <span className="text-gray-700 font-medium" data-id="element-4918">{line.line}</span>
                  <div className="flex items-center gap-4" data-id="element-4919">
                    <span className="text-gray-600" data-id="element-4920">
                      {line.currentOutput.toLocaleString()} /{' '}
                      {line.maxOutput.toLocaleString()} units
                    </span>
                    <span className={`font-bold ${line.utilized >= 85 ? 'text-green-600' : line.utilized >= 80 ? 'text-blue-600' : 'text-orange-600'}`} data-id="element-4921">
                      {line.utilized}%
                    </span>
                  </div>
                </div>)}
            </div>
          </div>
        </Card>
        {/* Operator Load Distribution */}
        <Card className="p-6" data-id="element-4922">
          <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-4923">
            Operator Load Distribution
          </h3>
          <ResponsiveContainer width="100%" height={350} data-id="element-4924">
            <RadarChart data={operatorRadarData} data-id="element-4925">
              <PolarGrid stroke="#e5e7eb" data-id="element-4926" />
              <PolarAngleAxis dataKey="metric" tick={{
              fontSize: 11
            }} data-id="element-4927" />
              <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{
              fontSize: 10
            }} data-id="element-4928" />
              <Radar name="Shift A" dataKey="ShiftA" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.3} data-id="element-4929" />
              <Radar name="Shift B" dataKey="ShiftB" stroke="#10b981" fill="#10b981" fillOpacity={0.3} data-id="element-4930" />
              <Radar name="Shift C" dataKey="ShiftC" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.3} data-id="element-4931" />
              <Legend data-id="element-4932" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} data-id="element-4933" />
            </RadarChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-4934">
            <div className="grid grid-cols-3 gap-4 text-sm" data-id="element-4935">
              {operatorLoadData.map((shift, index) => <div key={index} className="text-center" data-id="element-4936">
                  <div className="text-gray-600" data-id="element-4937">{shift.shift}</div>
                  <div className="text-lg font-bold text-gray-900" data-id="element-4938">
                    {shift.operators} ops
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-4939">
                    {shift.avgLoad}% avg load
                  </div>
                </div>)}
            </div>
          </div>
        </Card>
      </div>
      {/* Plant Utilization */}
      <Card className="p-6 mb-8" data-id="element-4940">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-4941">
          Plant Capacity Utilization (%)
        </h3>
        <ResponsiveContainer width="100%" height={300} data-id="element-4942">
          <BarChart data={plantUtilizationData} data-id="element-4943">
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-4944" />
            <XAxis dataKey="plant" tick={{
            fontSize: 11
          }} data-id="element-4945" />
            <YAxis tick={{
            fontSize: 11
          }} domain={[0, 100]} data-id="element-4946" />
            <Tooltip contentStyle={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px'
          }} data-id="element-4947" />
            <Legend data-id="element-4948" />
            <Bar dataKey="planned" fill="#3b82f6" name="Planned %" data-id="element-4949" />
            <Bar dataKey="actual" fill="#10b981" name="Actual %" data-id="element-4950" />
          </BarChart>
        </ResponsiveContainer>
      </Card>
      {/* Machine Loading */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-id="element-4951">
        <Card className="p-6" data-id="element-4952">
          <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-4953">
            Machine Loading Patterns
          </h3>
          <ResponsiveContainer width="100%" height={300} data-id="element-4954">
            <BarChart data={machineLoadingData} layout="vertical" data-id="element-4955">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-4956" />
              <XAxis type="number" domain={[0, 100]} tick={{
              fontSize: 11
            }} data-id="element-4957" />
              <YAxis dataKey="machine" type="category" width={100} tick={{
              fontSize: 11
            }} data-id="element-4958" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} data-id="element-4959" />
              <Legend data-id="element-4960" />
              <Bar dataKey="loading" fill="#3b82f6" name="Loading %" stackId="a" data-id="element-4961" />
              <Bar dataKey="idle" fill="#e5e7eb" name="Idle %" stackId="a" data-id="element-4962" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-6" data-id="element-4963">
          <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-4964">
            Shift-Level Performance
          </h3>
          <ResponsiveContainer width="100%" height={300} data-id="element-4965">
            <ComposedChart data={shiftPerformanceData} data-id="element-4966">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-4967" />
              <XAxis dataKey="shift" tick={{
              fontSize: 10
            }} data-id="element-4968" />
              <YAxis yAxisId="left" tick={{
              fontSize: 11
            }} data-id="element-4969" />
              <YAxis yAxisId="right" orientation="right" tick={{
              fontSize: 11
            }} data-id="element-4970" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} data-id="element-4971" />
              <Legend data-id="element-4972" />
              <Bar yAxisId="left" dataKey="utilization" fill="#10b981" name="Utilization %" data-id="element-4973" />
              <Line yAxisId="right" type="monotone" dataKey="output" stroke="#f59e0b" strokeWidth={2} name="Output (Units)" data-id="element-4974" />
            </ComposedChart>
          </ResponsiveContainer>
        </Card>
      </div>
      {/* Monthly Utilization Trend */}
      <Card className="p-6 mb-8" data-id="element-4975">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-4976">
          Monthly Utilization Trend
        </h3>
        <ResponsiveContainer width="100%" height={300} data-id="element-4977">
          <LineChart data={monthlyUtilizationData} data-id="element-4978">
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-4979" />
            <XAxis dataKey="month" tick={{
            fontSize: 12
          }} data-id="element-4980" />
            <YAxis domain={[0, 100]} tick={{
            fontSize: 12
          }} data-id="element-4981" />
            <Tooltip contentStyle={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px'
          }} data-id="element-4982" />
            <Legend data-id="element-4983" />
            <Line type="monotone" dataKey="utilization" stroke="#3b82f6" strokeWidth={3} name="Actual Utilization %" dot={{
            r: 4
          }} data-id="element-4984" />
            <Line type="monotone" dataKey="capacity" stroke="#ef4444" strokeWidth={2} strokeDasharray="5 5" name="Target Capacity %" data-id="element-4985" />
          </LineChart>
        </ResponsiveContainer>
      </Card>
      {/* Utilization Summary Table */}
      <Card className="p-6" data-id="element-4986">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-4987">
          Utilization Summary - Line & Shift Level
        </h3>
        <div className="overflow-x-auto" data-id="element-4988">
          <table className="w-full" data-id="element-4989">
            <thead data-id="element-4990">
              <tr className="border-b-2 border-gray-300" data-id="element-4991">
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4992">
                  Line
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4993">
                  Shift
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4994">
                  Planned %
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4995">
                  Actual %
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4996">
                  Idle %
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4997">
                  Utilization %
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4998">
                  Output
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4999">
                  Capacity
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5000">
                  Efficiency %
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5001">
                  Bottleneck
                </th>
              </tr>
            </thead>
            <tbody data-id="element-5002">
              {utilizationSummaryData.map((row, index) => <tr key={index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-5003">
                  <td className="py-3 px-4 text-sm font-medium text-gray-800" data-id="element-5004">
                    {row.line}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-5005">
                    {row.shift}
                  </td>
                  <td className="py-3 px-4 text-center text-sm text-gray-700" data-id="element-5006">
                    {row.planned}%
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-5007">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${row.actual >= 85 ? 'bg-green-100 text-green-700' : row.actual >= 80 ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`} data-id="element-5008">
                      {row.actual}%
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-5009">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${row.idle <= 15 ? 'bg-green-100 text-green-700' : row.idle <= 20 ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'}`} data-id="element-5010">
                      {row.idle}%
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center text-sm text-gray-700" data-id="element-5011">
                    {row.utilization}%
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-semibold text-gray-900" data-id="element-5012">
                    {row.output.toLocaleString()}
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-5013">
                    {row.capacity.toLocaleString()}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-5014">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${row.efficiency >= 95 ? 'bg-green-100 text-green-700' : row.efficiency >= 90 ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`} data-id="element-5015">
                      {row.efficiency.toFixed(1)}%
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-5016">
                    {row.bottleneck === 'None' ? <span className="text-green-600 font-medium" data-id="element-5017">None</span> : <span className="text-red-600 font-medium" data-id="element-5018">
                        {row.bottleneck}
                      </span>}
                  </td>
                </tr>)}
              {/* Average Row */}
              <tr className="bg-gray-100 border-t-2 border-gray-300 font-bold" data-id="element-5019">
                <td className="py-3 px-4 text-sm text-gray-900" colSpan={2} data-id="element-5020">
                  AVERAGE
                </td>
                <td className="py-3 px-4 text-center text-sm text-gray-900" data-id="element-5021">
                  85%
                </td>
                <td className="py-3 px-4 text-center" data-id="element-5022">
                  <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700" data-id="element-5023">
                    82%
                  </span>
                </td>
                <td className="py-3 px-4 text-center" data-id="element-5024">
                  <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700" data-id="element-5025">
                    18%
                  </span>
                </td>
                <td className="py-3 px-4 text-center text-sm text-gray-900" data-id="element-5026">
                  82%
                </td>
                <td className="py-3 px-4 text-sm text-right text-gray-900" data-id="element-5027">
                  3,667
                </td>
                <td className="py-3 px-4 text-sm text-right text-gray-900" data-id="element-5028">
                  4,000
                </td>
                <td className="py-3 px-4 text-center" data-id="element-5029">
                  <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700" data-id="element-5030">
                    91.7%
                  </span>
                </td>
                <td className="py-3 px-4 text-sm text-gray-900" data-id="element-5031">-</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="mt-6 pt-4 border-t border-gray-200" data-id="element-5032">
          <div className="grid grid-cols-5 gap-4 text-sm" data-id="element-5033">
            <div className="text-center" data-id="element-5034">
              <div className="text-gray-600" data-id="element-5035">Avg Utilization</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-5036">82%</div>
              <div className="text-xs text-gray-500" data-id="element-5037">across all shifts</div>
            </div>
            <div className="text-center" data-id="element-5038">
              <div className="text-gray-600" data-id="element-5039">Best Utilization</div>
              <div className="text-xl font-bold text-green-600" data-id="element-5040">
                Line 2, Shift B
              </div>
              <div className="text-xs text-gray-500" data-id="element-5041">89% utilization</div>
            </div>
            <div className="text-center" data-id="element-5042">
              <div className="text-gray-600" data-id="element-5043">Avg Idle Time</div>
              <div className="text-xl font-bold text-orange-600" data-id="element-5044">18%</div>
              <div className="text-xs text-gray-500" data-id="element-5045">opportunity area</div>
            </div>
            <div className="text-center" data-id="element-5046">
              <div className="text-gray-600" data-id="element-5047">Total Output</div>
              <div className="text-xl font-bold text-blue-600" data-id="element-5048">55,000</div>
              <div className="text-xs text-gray-500" data-id="element-5049">units today</div>
            </div>
            <div className="text-center" data-id="element-5050">
              <div className="text-gray-600" data-id="element-5051">Capacity Gap</div>
              <div className="text-xl font-bold text-red-600" data-id="element-5052">5,000</div>
              <div className="text-xs text-gray-500" data-id="element-5053">units available</div>
            </div>
          </div>
        </div>
      </Card>
    </div>;
};
export default CapacityUtilizationDashboard;